import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, FileText, Handshake, ChevronDown, ChevronUp, ShieldAlert } from "lucide-react";

import SEO, { buildFaqJsonLd } from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import FAQAccordion from "@/components/FAQAccordion";
import CommercialInquiryForm from "@/components/commercial/CommercialInquiryForm";
import {
  POSITIONING_COPY,
  COMMERCIAL_EXPERIENCE,
  COMMERCIAL_DIVISIONS,
  COMMERCIAL_SERVICE_CARDS,
  COMMERCIAL_FAQS,
} from "@/data/commercialContent";
import { SITE } from "@/lib/site";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Commercial" }];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function DivisionRow({ division, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = division.icon;
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`division-row-${division.code.toLowerCase().replace(/\s+/g, '-')}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-br-paper transition"
        aria-expanded={open}
      >
        <div className="w-10 h-10 rounded-md bg-br-paper-2 flex items-center justify-center text-br-bronze shrink-0">
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-br-bronze font-bold">{division.code}</div>
          <div className="font-display text-base tracking-widest uppercase text-br-black">{division.label}</div>
        </div>
        <span className="text-gray-400">
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <ul className="mt-3 grid sm:grid-cols-2 gap-1.5 text-sm text-gray-700">
            {division.items.map((i) => (
              <li key={i} className="flex items-start gap-2"><span className="text-br-bronze">▪</span> {i}</li>
            ))}
          </ul>
          {division.coordinationNote && (
            <div className="mt-3 flex items-start gap-2 bg-br-paper/60 border border-amber-200/60 rounded p-3">
              <ShieldAlert size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[11px] text-gray-700 leading-relaxed">{division.coordinationNote}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Commercial Services page — `/commercial`
 *
 * Sections (per spec):
 *   1. Hero (3 CTAs)
 *   2. Positioning copy
 *   3. Relevant Commercial Experience (imaging center / MRI)
 *   4. Commercial Services by Division (Div 01–32 + Commercial Maintenance)
 *   5. Commercial Service Categories (8 cards)
 *   6. FAQ
 *   7. Consultation form
 */
export default function Commercial() {
  return (
    <>
      <SEO
        title="Commercial General Contractor Austin TX | Black Ridge Construction"
        description="Black Ridge Construction provides commercial remodeling, facility updates, roofing, exterior work, maintenance, and division-based construction services in Austin and Central Texas."
        jsonLd={buildFaqJsonLd(COMMERCIAL_FAQS)}
      />

      {/* ──────────────────────── Hero ─────────────────────── */}
      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-20">
          <BreadcrumbNav dark items={BREADCRUMBS} />
          <div className="max-w-3xl mt-5">
            <div className="br-eyebrow text-br-bronze-2">Commercial Construction</div>
            <h1 className="br-headline text-4xl sm:text-5xl mt-3">Commercial Construction, Remodels &amp; Facility Updates</h1>
            <p className="mt-4 text-gray-300 leading-relaxed">
              Black Ridge Construction supports commercial clients, property owners, facility managers, investors, and business operators with remodels, repairs, exterior work, maintenance, and division-based construction services across Austin and Central Texas.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" onClick={() => scrollToId("commercial-form")} className="br-btn-primary" data-testid="commercial-hero-consult">
                <FileText size={14} /> Request a Commercial Consultation
              </button>
              <button type="button" onClick={() => scrollToId("commercial-services")} className="br-btn-secondary" data-testid="commercial-hero-services">
                View Commercial Services <ArrowRight size={14} />
              </button>
              <Link to="/subcontractor-partnerships" className="br-btn-secondary" data-testid="commercial-hero-partner">
                <Handshake size={14} /> Partner on a Scope
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────── Positioning ──────────────────── */}
      <section className="section-pad bg-br-paper">
        <div className="container-br grid lg:grid-cols-[1fr_1.4fr] gap-10">
          <div>
            <div className="br-eyebrow">Practical Commercial GC</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">A Real Contractor Approach to Commercial Work</h2>
          </div>
          <div className="prose-br">
            {POSITIONING_COPY.split("\n\n").map((p, i) => (
              <p key={i} className={i === 0 ? "text-gray-800 leading-relaxed" : "mt-3 text-gray-800 leading-relaxed"}>{p}</p>
            ))}
            <div className="mt-5 flex flex-wrap gap-3">
              <a href={`tel:${SITE.phoneE164}`} className="br-btn-dark" data-testid="commercial-positioning-call">
                <Phone size={14} /> Call {SITE.phone}
              </a>
              <button type="button" onClick={() => scrollToId("commercial-form")} className="br-btn-secondary" style={{ color: "#0a0a0a", borderColor: "#0a0a0a" }} data-testid="commercial-positioning-form">
                Request a Consultation <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── Relevant Experience (Imaging / MRI) ────────────────── */}
      <section className="section-pad bg-white" id="relevant-experience">
        <div className="container-br grid lg:grid-cols-2 gap-10">
          <div>
            <div className="br-eyebrow">Specialty Background</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Relevant Commercial Experience</h2>
            <p className="mt-3 text-gray-700 leading-relaxed">{COMMERCIAL_EXPERIENCE.intro}</p>
            <div className="mt-5 bg-br-paper border border-amber-200/60 rounded p-4 flex items-start gap-2" data-testid="commercial-disclaimer">
              <ShieldAlert size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[11px] text-gray-700 leading-relaxed">{COMMERCIAL_EXPERIENCE.disclaimer}</p>
            </div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2" data-testid="commercial-experience-list">
            {COMMERCIAL_EXPERIENCE.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-gray-800 bg-br-paper/60 border border-gray-100 rounded px-3 py-2">
                <span className="text-br-bronze">▪</span> {b}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ──────────────── Services by Division ─────────────────── */}
      <section className="section-pad bg-br-paper" id="services-by-division">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">By Division</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Commercial Services by Division</h2>
            <p className="mt-3 text-gray-700">Black Ridge organizes commercial work by CSI-style divisions for clear scoping, pricing, and coordination. Tap any division to view what we self-perform or coordinate.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-3" data-testid="divisions-list">
            {COMMERCIAL_DIVISIONS.map((d, idx) => (
              <DivisionRow key={d.code} division={d} defaultOpen={idx === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────── Commercial Service Categories ────────────────── */}
      <section className="section-pad bg-white" id="commercial-services">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">What We Do</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Commercial Service Categories</h2>
            <p className="mt-3 text-gray-700">Whether you need a full commercial GC or only one division of work, Black Ridge can fit the role.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="commercial-service-cards">
            {COMMERCIAL_SERVICE_CARDS.map((c, i) => (
              <div key={c.title} className="bg-white border border-gray-200 rounded-lg p-5 hover:border-br-bronze transition" data-testid={`commercial-service-card-${i}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-br-bronze font-bold">Service</div>
                <div className="font-display text-base tracking-widest uppercase mt-1 text-br-black leading-snug">{c.title}</div>
                <p className="mt-2 text-sm text-gray-700 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────── FAQ ──────────────────────── */}
      <section className="section-pad bg-br-paper" id="commercial-faq">
        <div className="container-br grid lg:grid-cols-[1fr_1.4fr] gap-10">
          <div>
            <div className="br-eyebrow">Commercial FAQ</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Common Questions</h2>
            <p className="mt-3 text-gray-700 leading-relaxed">Straightforward answers on how Black Ridge supports commercial scopes and clients.</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button type="button" onClick={() => scrollToId("commercial-form")} className="br-btn-dark" data-testid="commercial-faq-cta">
                Request a Consultation <ArrowRight size={14} />
              </button>
            </div>
          </div>
          <FAQAccordion faqs={COMMERCIAL_FAQS.map((f) => ({ q: f.q, a: f.a }))} />
        </div>
      </section>

      {/* ──────────────────── Form ────────────────────── */}
      <section className="section-pad bg-white">
        <div className="container-br">
          <CommercialInquiryForm />
        </div>
      </section>
    </>
  );
}
